import React, {Component, ReactElement} from 'react'
import {Location} from 'react-router-dom'
import styled from 'styled-components';
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants';
import {DisplayItemLabelDiv,} from '../../../styledComponents/DisplayItem';
import {PrimaryStyledButton, StyledSecondaryButton} from '../../../styledComponents/Button';
import {Nullable} from '../../../app/TypeHelpers';
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../styledComponents/ResponsiveConvenience';
import {StyledPageTitle} from '../../../components/reservations/components/CommonStyle';
import {Box} from '@mui/material';
import {judgeAuthorityCheck} from '../../common/util/Natutil';
import {NatGetResidentBankAccountInfo} from './repository/NatGetResidentBankAccountInfo'
import NatResidentBankAccountInfoRepo from './repository/NatResidentBankAccountInfoRepo'
import {HKConstants} from '../../../HKConstants'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {ResidentLoginInfoSessionStorageKey} from '../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {LOGIN_MODE, RESIDENT_USER_TYPE} from '../../../CodeMaster'
import NatResidentUserRepo from '../residentUser/repository/NatResidentUserRepo'
import {NatBankAccountInfo} from './NatResidentBankAccountInfoInterface'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Messages} from '../../../Messages';
import PreformattedDiv from '../../../styledComponents/PreformattedDiv';
import {WideMainContainer} from '../../../styledComponents/ContentsArea';

interface Props {
    condominiumCd: string
    residentUserRepo: NatResidentUserRepo
    bankAccountInfoRepo: NatResidentBankAccountInfoRepo,
    location: Location
}

interface States {
    bankAccountInfo: Nullable<NatBankAccountInfo>,
    mainUserName: string,
    isSubUser: boolean,
    isAdminUser: boolean,
    haveAuthViewAccountInfo: boolean,
    haveAuthApplyAccountInfo: boolean,
    isLoadCompleted: boolean,
    maintenanceMessage: string,
}

class NatResidentBankAccountInfoPage extends Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props);

        const userType = SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_USER_RESIDENT_USER_TYPE)
        const isAdminUser = SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_MODE) === LOGIN_MODE.ADMIN.value

        this.state = {
            bankAccountInfo: null,
            mainUserName: '',
            isSubUser: userType === RESIDENT_USER_TYPE.SUB.value,
            isAdminUser: isAdminUser,
            haveAuthViewAccountInfo:
                judgeAuthorityCheck(HKConstants.CMN.AUTH.VIEW_ACCOUNT_INFORMATION),
            haveAuthApplyAccountInfo:
                judgeAuthorityCheck(HKConstants.CMN.AUTH.APPLY_ACCOUNT_TRANSFER) || isAdminUser,
            isLoadCompleted: false,
            maintenanceMessage: '',
        };

        this.accountTransferButtonClick = this.accountTransferButtonClick.bind(this);
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading();
        const {haveAuthViewAccountInfo, isSubUser} = this.state
        try {
            // 口座情報を取得
            if (haveAuthViewAccountInfo) {
                const bankAccountInfo: Nullable<NatGetResidentBankAccountInfo> = await this.props.asyncCallFunction(
                    this.props.bankAccountInfoRepo.getResidentBankAccountInfo.bind(
                        this.props.bankAccountInfoRepo),
                    (httpErrorOrNetworkError) => {
                        if (httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.ESYCC0027.id) {
                            this.setState({
                                maintenanceMessage: Messages.ESYCC0027.message
                            })
                        }
                    })

                if (bankAccountInfo?.details) {
                    this.setState({
                        bankAccountInfo: bankAccountInfo.details
                    })
                }
            }
            // メインユーザの氏名を取得
            if (!haveAuthViewAccountInfo && isSubUser) {
                await this.setupMainUserName()
            }
        } finally {
            this.props.eventEmitter.closeLoading();
            this.setState({
                isLoadCompleted: true
            })
        }
    }

    async setupMainUserName(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        const {residentUserRepo, eventEmitter} = this.props
        try {
            const residentUserResponse = await this.props.asyncCallFunction(
                residentUserRepo.getResidentUserLogined.bind(residentUserRepo),
                (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.ESYCC0027.id) {
                        this.setState({
                            maintenanceMessage: Messages.ESYCC0027.message
                        })
                    } else {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    }
                }
            )
            if (residentUserResponse?.details?.mainResidentUserInfo) {
                const {familyName, firstName} = residentUserResponse.details.mainResidentUserInfo

                this.setState({
                    mainUserName: `${familyName} ${firstName}`
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    // 口座振替登録へ
    accountTransferButtonClick(): void {
        const {condominiumCd} = this.props
        this.props.navigate(
            `/resident/condominiums/${condominiumCd}/applications/account-transfer/receiving-agency/redirect`
        )
    }

    renderMaintenanceMassageArea(): Nullable<ReactElement> {
        const {maintenanceMessage} = this.state
        return (
            <WhiteMessageArea>
                <PreformattedDiv>{maintenanceMessage}</PreformattedDiv>
            </WhiteMessageArea>
        )
    }

    renderBankAccountInfo(): ReactElement {
        return (
            <>
                {this.state.maintenanceMessage ?
                    this.renderMaintenanceMassageArea()
                    : this.renderBankAccountInfoList()
                }
            </>
        )
    }

    renderBankAccountInfoList(): ReactElement {
        const {bankAccountInfo, isAdminUser} = this.state
        return (
            <>
                <ItemHeading>口座情報</ItemHeading>
                {bankAccountInfo ?
                    <>
                        <ItemDiv>
                            <GridContainer alignItems={'flex-start'}>
                                <GridItem over={3}>
                                    <DisplayItemLabelDiv>
                                        <Box mr={'5px'}>金融機関名</Box>
                                    </DisplayItemLabelDiv>
                                </GridItem>
                                <GridItem over={9}>
                                    <TextContents>
                                        {isAdminUser
                                            ? 'ー'
                                            : bankAccountInfo.bankName + bankAccountInfo.branchName}
                                    </TextContents>
                                </GridItem>
                            </GridContainer>
                        </ItemDiv>
                        <BlockSpacer height={'5px'}/>
                        <ItemDiv>
                            <GridContainer alignItems={'flex-start'}>
                                <GridItem over={3}><DisplayItemLabelDiv><Box
                                    mr={'5px'}>口座番号</Box></DisplayItemLabelDiv>
                                </GridItem>
                                <GridItem over={9}>
                                    <TextContents>
                                        {isAdminUser
                                            ? 'ー'
                                            : bankAccountInfo.depositType + bankAccountInfo.bankAccountNumber}
                                    </TextContents>
                                </GridItem>
                            </GridContainer>
                        </ItemDiv>
                        <BlockSpacer height={'5px'}/>
                        <ItemDiv>
                            <GridContainer alignItems={'flex-start'}>
                                <GridItem over={3}><DisplayItemLabelDiv><Box
                                    mr={'5px'}>口座名義</Box></DisplayItemLabelDiv>
                                </GridItem>
                                <GridItem over={9}>
                                    <TextContents>{bankAccountInfo.accountPersonHolder}</TextContents>
                                </GridItem>
                            </GridContainer>
                        </ItemDiv>
                    </>
                    : <ItemDiv>登録されていません</ItemDiv>
                }
            </>
        )
    }

    renderNotHaveAuthViewAccountInfo(): Nullable<ReactElement> {
        const {mainUserName} = this.state
        return (<>
                <Box fontWeight={FontWeights.Bold} color={Colors.Alert}>
                    本機能はご利用いただけません。<br/>
                    ご利用になる場合は、メインユーザの{mainUserName}さんにご確認ください。<br/>
                    メインユーザアカウントの「個人設定＞ユーザ管理」から利用制限の設定を変更できます。
                </Box>
                <BlockSpacer height={'20px'}/>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        const {haveAuthViewAccountInfo, haveAuthApplyAccountInfo} = this.state
        return (
            <>
                <BlockSpacer height={'30px'}/>
                <StyledPageTitle>
                    口座振替の情報
                </StyledPageTitle>
                <BlockSpacer height={'20px'}/>
                {!haveAuthViewAccountInfo && this.renderNotHaveAuthViewAccountInfo()}
                自動引き落としにご指定の口座情報を確認できます。<br/>
                お手続き状況により、最新の設定情報が反映されるまでにお時間がかかる場合があります。
                <BlockSpacer height={'20px'}/>
                {haveAuthViewAccountInfo && this.renderBankAccountInfo()}
                {haveAuthApplyAccountInfo ?
                    <>
                        <BlockSpacer height={'20px'}/>
                        <PrimaryStyledButton onClick={this.accountTransferButtonClick}>
                            口座振替登録へ
                        </PrimaryStyledButton>
                    </>
                    : <></>
                }
                {this.state.maintenanceMessage &&
                    <>
                        {haveAuthApplyAccountInfo ? <Box height={'30px'}/> : <Box height={'20px'}/>}
                        <StyledSecondaryButton
                            onClick={() => this.props.navigate({
                                pathname: '/resident/mypage',
                            })}>
                            ホームへ戻る
                        </StyledSecondaryButton>
                    </>
                }
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBankAccountInfoPage)

const ItemHeading = styled.div`
    background-color: ${Colors.AccentLight};
    ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
    font-size: ${FontSize.Body};
    padding: 6px 15px 6px;
    box-sizing: border-box;
    overflow: hidden;
`
const ItemDiv = styled.div`
    background-color: ${Colors.Surface};
    ${currentMedia.lessThan('sp')`
    margin: 0px -15px;
  `}
    padding: 20px 15px 20px 15px;
`
const TextContents = styled.div`
    font-weight: ${FontWeights.Bold};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`
const WhiteMessageArea = styled(WideMainContainer)`
    padding: 15px;
`
